import { FeatureListingModel } from 'src/api/models';
import { FeatureCategory, ParkingHighlight } from '../../types';
import {
  toDisplayValue,
  parseParkingSpots,
  hasNoLaundryAppliances,
} from '../../utils';

type ParkingType = 'Street Permit' | 'Street Parking';
type ParkingInfo = { spots: number; type?: ParkingType };

const featureNameToParkingType: Partial<Record<ParkingHighlight, ParkingType>> =
  {
    'On-Street Permit': 'Street Permit',
    'Alley / Off-Street Parking': 'Street Parking',
  };
const parkingWithSpots: ParkingHighlight[] = [
  'Garage Parking',
  'Driveway Parking',
  'Lot Parking',
  'Carport Parking',
]; // these are feature names

export function getParking(features: FeatureListingModel[]): ParkingInfo {
  const parkingFeatures = features.filter(
    (feature) => (feature.category as FeatureCategory) === 'Parking',
  );
  return parkingFeatures.reduce<ParkingInfo>(
    (prevParking: ParkingInfo, currentFeat: FeatureListingModel) => {
      const featureName = currentFeat.name! as ParkingHighlight;
      const currentParking = { ...prevParking };
      const { attributes = {} } = currentFeat;
      if (
        parkingWithSpots.includes(featureName) ||
        (featureName === 'Parking' &&
          (attributes.parkingType || attributes.ParkingType))
      ) {
        currentParking.spots += parseParkingSpots(attributes);
        return currentParking;
      }
      if (featureNameToParkingType[featureName])
        currentParking.type = featureNameToParkingType[featureName];
      return currentParking;
    },
    { spots: 0 },
  );
}

export function getHeaterType(
  features: FeatureListingModel[],
): string | undefined {
  const heatFeature = features.find((feature) => feature.name === 'Heat');
  const attributes = heatFeature?.attributes;
  return (
    toDisplayValue(attributes?.heatType || attributes?.HeatType) ||
    toDisplayValue(attributes?.heatSource || attributes?.HeatSource) ||
    toDisplayValue(attributes?.heatDistribution || attributes?.HeatDistribution)
  );
}

export function getLaundryType(
  features: FeatureListingModel[],
): 'In Unit' | 'Communal' | 'Hookup' | undefined {
  const laundryFeatures = features.filter(
    (feature) => (feature.category as FeatureCategory) === 'Laundry',
  );

  if (!laundryFeatures.length) return;

  if (laundryFeatures.some((feature) => feature.name === 'Laundry Hookup'))
    return 'Hookup';

  if (laundryFeatures.every(hasNoLaundryAppliances)) return;

  return laundryFeatures.some((feature) => !feature.isCommunalFeature)
    ? 'In Unit'
    : 'Communal';
}
